import { isCustomer, isSupplier, isVasTeam, isAdminOrStaff } from 'app/main/apps/common/AuraFunctions';

/**
 * PGSS-5381 — what may this list screen show, rather than who is looking at it.
 *
 * Role identity is asked directly at the point of use 647 times across 100+ files; `Orders.js` alone
 * carries 66 such checks. Two problems come from that, and both have bitten recently:
 *
 *   1. A gate like `!isCustomer(role) && !isVasTeam(role)` says nothing about WHY. Getting one
 *      backwards ships a control to the wrong audience, which is what happened on PGSS-5371 and
 *      PGSS-5352 and what review caught on FE #2462.
 *   2. "Who can see X?" cannot be answered without grepping four helpers across the app.
 *
 * This derives the answer once, so call sites read a name that says what it controls. It is a pure
 * function on purpose: there is no component test harness in this repo, so behaviour expressed in
 * JSX cannot be tested, but behaviour expressed here can.
 *
 * NOT a permission system. `isManage` is a real permission lookup
 * (`FuseUtils.hasPermission(authPermissions.manage, role)`), so it is passed IN as `canManage`
 * rather than recomputed — this decides visibility, it does not grant authority.
 *
 * Adopt incrementally. Converting a call site is only correct if the resulting audience is
 * identical; every capability below mirrors the gate it replaced exactly, and the tests assert that
 * rather than asserting what the rules arguably ought to be.
 */

/** Roles that see the staff-facing list controls. Customers and the VAS team are excluded. */
function isStaffFacing(role) {
  return !isCustomer(role) && !isVasTeam(role);
}

/**
 * @param user           the logged-in user ({ role })
 * @param type           list type — order | quote | rfq | rfd | completed | invoice | container
 * @param options.canManage     result of the `manage` permission lookup (isManage at the call site)
 * @param options.isReorderMode  reorder view hides the ordinary filters
 */
export function getListCapabilities(user, type, options = {}) {
  const role = user?.role;
  const { canManage = false, isReorderMode = false, isSupplierInvoice = false } = options;

  // Every capability below is false in reorder mode, matching the existing `!isReorderMode` on each
  // gate. Kept as one guard rather than repeated per capability so it cannot be forgotten on a new one.
  const inOrdinaryView = !isReorderMode;

  return {
    /** Include Invoice Status — staff-facing, order list only. */
    filterIncludeInvoice: inOrdinaryView && type === 'order' && isStaffFacing(role),

    /** Include Archive — staff-facing, order and container lists. */
    filterIncludeArchive:
      inOrdinaryView && ['order', 'container'].includes(type) && isStaffFacing(role),

    /**
     * Include Cancelled Invoices (PGSS-5371) — staff-facing, both invoice lists.
     * Customers are excluded because their portal already filters cancelled invoices server-side,
     * so the control would do nothing for them.
     *
     * PGSS-5371: supplier invoices are excluded too. There is no action anywhere that cancels a
     * supplier invoice, so the control offers staff a filter that can only ever return nothing.
     * Raised by QA after the first version gated on role alone — and the supplier invoice list is
     * staff-facing, so it slipped through.
     */
    filterCancelledInvoices:
      inOrdinaryView && type === 'invoice' && isStaffFacing(role) && !isSupplierInvoice,

    /**
     * Include Waiting for Approval (PGSS-5352) — CUSTOMERS only, order list.
     *
     * The inverse audience of every other capability here: the waiting-for-approval exclusion is a
     * customer-list rule (PGSS-5094), so staff already see those statuses and the option would do
     * nothing for them. Worth stating plainly, because `isCustomer` where the neighbours read
     * `!isCustomer` is exactly the kind of gate that gets copied wrong.
     */
    filterWaitingApproval: inOrdinaryView && type === 'order' && isCustomer(role),

    /** Show only VAS — requires the manage permission, not merely a staff role. */
    filterShowOnlyVAS:
      inOrdinaryView && ['order', 'quote', 'rfq'].includes(type) && canManage,

    /**
     * Actionable only — admin/staff/sales, which is narrower than "staff-facing" (a supplier is
     * staff-facing but not admin/staff). Boolean() because isAdminOrStaff is `user && ...`, so it
     * returns undefined rather than false when there is no user.
     */
    filterActionableOnly: Boolean(
      inOrdinaryView && ['order', 'quote', 'rfq'].includes(type) && isAdminOrStaff(user)
    ),

    /** Supplier's own in-production / fulfilled views. */
    filterSupplierOrderType:
      ['in-production', 'fulfilled'].includes(type) && isSupplier(role),
  };
}

export default getListCapabilities;
